import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ApiUpdateStop } from '#api/model/types.ts';
import { LocationFormView } from '#components/order/CreateOrderDialog/LocationForm.tsx';
import type { Stop } from '#domain/Stop.ts';

import {
    type EditStopFormData,
    EditStopFormValidation,
    mapEditStopFormDataToStop,
    mapEditStopFormDataToUpdateStop,
    mapStopToEditStopFormData,
} from './EditStopFormState.ts';
import { mapStopTypeToAddressType } from './mapStopTypeToAddressType.ts';

type EditStopDialogProps = {
    stop?: Stop;
    isOpen: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onSubmit: (stopUpdate: ApiUpdateStop, updatedStop: Stop) => Promise<void> | void;
    validateStop?: (updatedStop: Stop) => { field: 'startDate' | 'endDate'; message: string } | undefined;
};

export const EditStopDialog = ({
    stop,
    isOpen,
    isLoading = false,
    onClose,
    onSubmit,
    validateStop,
}: EditStopDialogProps) => {
    const intl = useIntl();
    const formContext = useForm<EditStopFormData>({
        resolver: zodResolver(EditStopFormValidation),
        mode: 'onTouched',
        values: stop ? mapStopToEditStopFormData(stop) : undefined,
    });
    const { handleSubmit, setError } = formContext;
    const addressType = mapStopTypeToAddressType(stop?.type);

    const submit = handleSubmit(async formValues => {
        if (!stop) {
            return;
        }
        const updatedStop = mapEditStopFormDataToStop(formValues, stop);
        const validationError = validateStop?.(updatedStop);

        if (validationError) {
            setError(validationError.field, { type: 'manual', message: validationError.message });
            return;
        }

        await onSubmit(mapEditStopFormDataToUpdateStop(formValues, stop), updatedStop);
        onClose();
    });

    return (
        <FormProvider {...formContext}>
            <Dialog
                show={isOpen}
                title={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.stop.edit.title',
                    defaultMessage: 'Edit stop',
                })}
                body={
                    <LocationFormView
                        addressType={addressType}
                        allowPastDates
                        commentName='comment'
                        showCommentInitially={Boolean(stop?.comment)}
                    />
                }
                footer={
                    <ButtonToolbar alignment='right'>
                        <Button bsStyle='default' onClick={onClose}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.cancel',
                                defaultMessage: 'Cancel',
                            })}
                        </Button>
                        <Button bsStyle='primary' onClick={() => submit()} disabled={isLoading}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.save',
                                defaultMessage: 'Save changes',
                            })}
                        </Button>
                    </ButtonToolbar>
                }
                onClose={onClose}
                bsSize='md'
                showCloseButton
            />
        </FormProvider>
    );
};
